// Internationalization translations for 16 languages
export const translations = {
  en: {
    nav: {
      whatWeDo: "What We Do",
      experience: "Experience",
      portfolio: "Portfolio",
      philosophy: "Philosophy",
      contact: "Contact"
    },
    hero: {
      tagline: "Beyond AI",
      subheading: "Applied intelligence, architecture, and execution for real-world systems.",
      portfolioSubheading: "Our product catalog.",
      viewExperience: "View Experience",
      contact: "Contact"
    },
    whatWeDo: {
      title: "What We Do",
      subtitle: "Senior technical consultancy delivering architecture-driven solutions for complex systems.",
      services: {
        mobile: {
          title: "Mobile Architecture",
          description: "Native iOS, Android, and cross-platform development with production-grade architecture."
        },
        cloud: {
          title: "Cloud Architecture",
          description: "AWS, Google Cloud, and Azure solutions designed for scale and reliability."
        },
        ai: {
          title: "AI & Applied Intelligence",
          description: "Practical, production-grade AI systems. No experimental hype—real solutions."
        },
        backend: {
          title: "Backend & Distributed Systems",
          description: "High-availability architectures built for performance and maintainability."
        },
        security: {
          title: "Security & Compliance",
          description: "Enterprise-grade security for regulated environments and sensitive data."
        },
        financial: {
          title: "Financial Systems",
          description: "Banking, payments, and financial infrastructure with compliance at the core."
        },
        strategy: {
          title: "Technical Strategy",
          description: "Architecture consulting and strategic guidance for technology decisions."
        }
      }
    },
    experience: {
      title: "Track Record",
      subtitle: "Two decades of building systems that operate at scale.",
      areas: {
        mobile: "Mobile Platforms",
        mobileDesc: "Native iOS & Android development",
        cloud: "Cloud-Native Systems",
        cloudDesc: "Scalable infrastructure design",
        ai: "Applied AI",
        aiDesc: "Production-grade intelligence",
        financial: "Financial Systems",
        financialDesc: "Banking & payments expertise",
        security: "Security Architecture",
        securityDesc: "Authentication & compliance",
        highAvail: "High Availability",
        highAvailDesc: "Regulated environment design"
      }
    },
    portfolio: {
      title: "Portfolio",
      subtitle: "Selected work from our practice.",
      wishio: {
        title: "Wishio",
        description: "Multi-platform mobile application focused on reminders, personal moments, and thoughtful interactions.",
        tags: ["iOS", "Android", "Mobile Architecture"]
      },
      expeditio: {
        title: "Expeditio",
        description: "AI-powered travel planning app with offline maps, document storage, photo organization, smart hotel suggestions, and a community for tips and reviews.",
        tags: ["iOS", "Android", "AI", "Travel"]
      },
      jason: {
        title: "Jason and the Argonauts",
        description: "Single-player mythological expedition strategy-RPG for iOS, Android and web. A procedurally generated Aegean, a deterministic game engine, and applied AI that renders narration without ever owning game state.",
        tags: ["iOS", "Android", "Web", "Game Engine", "Applied AI"],
        badge: "IN DEVELOPMENT"
      },
      viewProject: "View Project",
      comingSoon: "More projects coming soon"
    },
    philosophy: {
      title: "Our Philosophy",
      principles: {
        noHype: {
          title: "No Hype-Driven Solutions",
          description: "Technology choices based on merit, not trends."
        },
        tool: {
          title: "Technology as a Tool",
          description: "Systems serve business objectives, not the other way around."
        },
        architecture: {
          title: "Architecture First",
          description: "Decisions grounded in risk analysis and long-term value."
        },
        production: {
          title: "Built for Production",
          description: "Systems designed to operate reliably in the real world."
        }
      }
    },
    contact: {
      title: "Contact",
      subtitle: "Let's discuss how we can help with your next project.",
      name: "Name",
      email: "Email",
      message: "Message",
      send: "Send Message",
      sending: "Sending...",
      success: "Message sent successfully.",
      error: "Failed to send message. Please try again."
    },
    footer: {
      rights: "All rights reserved.",
      domain: "interware.dev"
    },
    portfolioIntro: {
      hero: {
        title: "Introduction by Gonzalo Duranona",
        subtitle: "Technical architecture, mobile engineering leadership, and delivery of scalable products.",
        ctaPrimary: "Contact Gonzalo",
        ctaSecondary: "Visit company site",
        privateNote: "Private link."
      },
      whatWeDo: {
        title: "Engineering leadership and hands-on delivery",
        description: "Interware helps teams design and ship reliable mobile and cloud systems. We combine pragmatic architecture with strong execution: security, performance, clean delivery processes, and product-ready engineering."
      },
      focusAreas: {
        title: "Focus areas",
        items: [
          "Mobile architecture (iOS/Android native, Swift/SwiftUI, Kotlin, React Native)",
          "Backend & cloud (APIs, microservices, AWS/GCP/Azure)",
          "Security & compliance (hardening, attestation, certificate pinning strategies)",
          "Delivery acceleration (CI/CD, release automation, observability)",
          "Product engineering (from prototype to production)"
        ]
      },
      projects: {
        title: "Recent projects",
        items: [
          {
            title: "Mobile Banking Delivery",
            description: "Offline resilience, error handling strategy, security hardening, scalable release workflows."
          },
          {
            title: "Enterprise Mobile Platforms",
            description: "Architecture patterns, modularization, performance tuning, and long-term maintainability."
          },
          {
            title: "Cloud Services & APIs",
            description: "Secure APIs, infrastructure automation, and operational visibility."
          },
          {
            title: "AI-enabled Product Features",
            description: "Applied AI for content generation and product workflows, with responsible controls."
          }
        ]
      },
      workingStyle: {
        title: "Direct, clear, and execution-driven",
        description: "I work closely with stakeholders and engineering teams, translating business goals into concrete architecture and delivery plans. I'm comfortable leading teams, unblocking execution, and owning outcomes end-to-end."
      },
      contact: {
        title: "Let's connect",
        description: "If you'd like to discuss a project, a leadership role, or a collaboration, reach out.",
        email: "gonzalo@interware.dev",
        linkedin: "www.linkedin.com/in/gonzalodd",
        calendar: "calendly.com/duranona-gonzalo"
      },
      footer: "© Interware — Private portfolio link. Not for public distribution."
    }
  },
  es: {
    nav: {
      whatWeDo: "Qué Hacemos",
      experience: "Experiencia",
      portfolio: "Portfolio",
      philosophy: "Filosofía",
      contact: "Contacto"
    },
    hero: {
      tagline: "Más Allá de la IA",
      subheading: "Inteligencia aplicada, arquitectura y ejecución para sistemas del mundo real.",
      portfolioSubheading: "Catálogo de nuestros productos.",
      viewExperience: "Ver Experiencia",
      contact: "Contacto"
    },
    whatWeDo: {
      title: "Qué Hacemos",
      subtitle: "Consultoría técnica senior ofreciendo soluciones orientadas a la arquitectura para sistemas complejos.",
      services: {
        mobile: {
          title: "Arquitectura Móvil",
          description: "Desarrollo nativo iOS, Android y multiplataforma con arquitectura de nivel producción."
        },
        cloud: {
          title: "Arquitectura Cloud",
          description: "Soluciones AWS, Google Cloud y Azure diseñadas para escala y fiabilidad."
        },
        ai: {
          title: "IA e Inteligencia Aplicada",
          description: "Sistemas de IA prácticos y de nivel producción. Sin hype experimental—soluciones reales."
        },
        backend: {
          title: "Backend y Sistemas Distribuidos",
          description: "Arquitecturas de alta disponibilidad construidas para rendimiento y mantenibilidad."
        },
        security: {
          title: "Seguridad y Cumplimiento",
          description: "Seguridad de nivel empresarial para entornos regulados y datos sensibles."
        },
        financial: {
          title: "Sistemas Financieros",
          description: "Banca, pagos e infraestructura financiera con cumplimiento en el núcleo."
        },
        strategy: {
          title: "Estrategia Técnica",
          description: "Consultoría de arquitectura y orientación estratégica para decisiones tecnológicas."
        }
      }
    },
    experience: {
      title: "Trayectoria",
      subtitle: "Dos décadas construyendo sistemas que operan a escala.",
      areas: {
        mobile: "Plataformas Móviles",
        mobileDesc: "Desarrollo nativo iOS y Android",
        cloud: "Sistemas Cloud-Native",
        cloudDesc: "Diseño de infraestructura escalable",
        ai: "IA Aplicada",
        aiDesc: "Inteligencia de nivel producción",
        financial: "Sistemas Financieros",
        financialDesc: "Experiencia en banca y pagos",
        security: "Arquitectura de Seguridad",
        securityDesc: "Autenticación y cumplimiento",
        highAvail: "Alta Disponibilidad",
        highAvailDesc: "Diseño para entornos regulados"
      }
    },
    portfolio: {
      title: "Portfolio",
      subtitle: "Trabajos seleccionados de nuestra práctica.",
      wishio: {
        title: "Wishio",
        description: "Aplicación móvil multiplataforma enfocada en recordatorios, momentos personales e interacciones significativas.",
        tags: ["iOS", "Android", "Arquitectura Móvil"]
      },
      expeditio: {
        title: "Expeditio",
        description: "App de planificación de viajes con IA, mapas offline, almacenamiento de documentos, organización de fotos, sugerencias inteligentes de hoteles y una comunidad para consejos y reseñas.",
        tags: ["iOS", "Android", "IA", "Viajes"]
      },
      jason: {
        title: "Jasón y los argonautas",
        description: "RPG de estrategia y expedición mitológica para un jugador, en iOS, Android y web. Un Egeo generado proceduralmente, un motor de juego determinista e IA aplicada que narra sin gobernar nunca el estado del juego.",
        tags: ["iOS", "Android", "Web", "Motor de Juego", "IA Aplicada"],
        badge: "EN DESARROLLO"
      },
      viewProject: "Ver Proyecto",
      comingSoon: "Más proyectos próximamente"
    },
    philosophy: {
      title: "Nuestra Filosofía",
      principles: {
        noHype: {
          title: "Sin Soluciones Basadas en Hype",
          description: "Decisiones tecnológicas basadas en mérito, no en tendencias."
        },
        tool: {
          title: "Tecnología como Herramienta",
          description: "Los sistemas sirven a los objetivos del negocio, no al revés."
        },
        architecture: {
          title: "Arquitectura Primero",
          description: "Decisiones fundamentadas en análisis de riesgo y valor a largo plazo."
        },
        production: {
          title: "Construido para Producción",
          description: "Sistemas diseñados para operar de forma fiable en el mundo real."
        }
      }
    },
    contact: {
      title: "Contacto",
      subtitle: "Hablemos sobre cómo podemos ayudar con su próximo proyecto.",
      name: "Nombre",
      email: "Correo electrónico",
      message: "Mensaje",
      send: "Enviar Mensaje",
      sending: "Enviando...",
      success: "Mensaje enviado exitosamente.",
      error: "Error al enviar el mensaje. Por favor intente de nuevo."
    },
    footer: {
      rights: "Todos los derechos reservados.",
      domain: "interware.dev"
    },
    portfolioIntro: {
      hero: {
        title: "Presentación por Gonzalo Durañona",
        subtitle: "Arquitectura técnica, liderazgo en ingeniería móvil y entrega de productos escalables.",
        ctaPrimary: "Contactar a Gonzalo",
        ctaSecondary: "Visitar sitio web",
        privateNote: "Enlace privado."
      },
      whatWeDo: {
        title: "Liderazgo de ingeniería y entrega práctica",
        description: "Interware ayuda a equipos a diseñar y entregar sistemas móviles y cloud confiables. Combinamos arquitectura pragmática con ejecución sólida: seguridad, rendimiento, procesos de entrega limpios e ingeniería lista para producción."
      },
      focusAreas: {
        title: "Áreas de enfoque",
        items: [
          "Arquitectura móvil (iOS/Android nativo, Swift/SwiftUI, Kotlin, React Native)",
          "Backend & cloud (APIs, microservicios, AWS/GCP/Azure)",
          "Seguridad & cumplimiento (hardening, attestation, estrategias de certificate pinning)",
          "Aceleración de entregas (CI/CD, automatización de releases, observabilidad)",
          "Ingeniería de producto (del prototipo a producción)"
        ]
      },
      projects: {
        title: "Proyectos recientes",
        items: [
          {
            title: "Entrega de Banca Móvil",
            description: "Resiliencia offline, estrategia de manejo de errores, hardening de seguridad, flujos de release escalables."
          },
          {
            title: "Plataformas Móviles Empresariales",
            description: "Patrones de arquitectura, modularización, optimización de rendimiento y mantenibilidad a largo plazo."
          },
          {
            title: "Servicios Cloud & APIs",
            description: "APIs seguras, automatización de infraestructura y visibilidad operacional."
          },
          {
            title: "Funcionalidades con IA",
            description: "IA aplicada para generación de contenido y flujos de producto, con controles responsables."
          }
        ]
      },
      workingStyle: {
        title: "Directo, claro y orientado a la ejecución",
        description: "Trabajo de cerca con stakeholders y equipos de ingeniería, traduciendo objetivos de negocio en planes concretos de arquitectura y entrega. Me siento cómodo liderando equipos, desbloqueando la ejecución y siendo responsable de los resultados de principio a fin."
      },
      contact: {
        title: "Conectemos",
        description: "Si desea discutir un proyecto, un rol de liderazgo o una colaboración, contácteme.",
        email: "gonzalo@interware.dev",
        linkedin: "www.linkedin.com/in/gonzalodd",
        calendar: "calendly.com/duranona-gonzalo"
      },
      footer: "© Interware — Enlace privado de portfolio. No para distribución pública."
    }
  },
  pt: {
    nav: {
      whatWeDo: "O Que Fazemos",
      experience: "Experiência",
      portfolio: "Portfolio",
      philosophy: "Filosofia",
      contact: "Contato"
    },
    hero: {
      tagline: "Além da IA",
      subheading: "Inteligência aplicada, arquitetura e execução para sistemas do mundo real.",
      portfolioSubheading: "Catálogo dos nossos produtos.",
      viewExperience: "Ver Experiência",
      contact: "Contato"
    },
    whatWeDo: {
      title: "O Que Fazemos",
      subtitle: "Consultoria técnica sênior entregando soluções orientadas à arquitetura para sistemas complexos.",
      services: {
        mobile: {
          title: "Arquitetura Mobile",
          description: "Desenvolvimento nativo iOS, Android e multiplataforma com arquitetura de nível produção."
        },
        cloud: {
          title: "Arquitetura Cloud",
          description: "Soluções AWS, Google Cloud e Azure projetadas para escala e confiabilidade."
        },
        ai: {
          title: "IA e Inteligência Aplicada",
          description: "Sistemas de IA práticos e de nível produção. Sem hype experimental—soluções reais."
        },
        backend: {
          title: "Backend e Sistemas Distribuídos",
          description: "Arquiteturas de alta disponibilidade construídas para performance e manutenibilidade."
        },
        security: {
          title: "Segurança e Conformidade",
          description: "Segurança de nível empresarial para ambientes regulados e dados sensíveis."
        },
        financial: {
          title: "Sistemas Financeiros",
          description: "Bancos, pagamentos e infraestrutura financeira com conformidade no núcleo."
        },
        strategy: {
          title: "Estratégia Técnica",
          description: "Consultoria de arquitetura e orientação estratégica para decisões tecnológicas."
        }
      }
    },
    experience: {
      title: "Trajetória",
      subtitle: "Duas décadas construindo sistemas que operam em escala.",
      areas: {
        mobile: "Plataformas Mobile",
        mobileDesc: "Desenvolvimento nativo iOS e Android",
        cloud: "Sistemas Cloud-Native",
        cloudDesc: "Design de infraestrutura escalável",
        ai: "IA Aplicada",
        aiDesc: "Inteligência de nível produção",
        financial: "Sistemas Financeiros",
        financialDesc: "Expertise em bancos e pagamentos",
        security: "Arquitetura de Segurança",
        securityDesc: "Autenticação e conformidade",
        highAvail: "Alta Disponibilidade",
        highAvailDesc: "Design para ambientes regulados"
      }
    },
    portfolio: {
      title: "Portfolio",
      subtitle: "Trabalhos selecionados da nossa prática.",
      wishio: {
        title: "Wishio",
        description: "Aplicativo móvel multiplataforma focado em lembretes, momentos pessoais e interações significativas.",
        tags: ["iOS", "Android", "Arquitetura Mobile"]
      },
      expeditio: {
        title: "Expeditio",
        description: "App de planejamento de viagens com IA, mapas offline, armazenamento de documentos, organização de fotos, sugestões inteligentes de hotéis e uma comunidade para dicas e avaliações.",
        tags: ["iOS", "Android", "IA", "Viagens"]
      },
      jason: {
        title: "Jasão e os argonautas",
        description: "RPG de estratégia e expedição mitológica para um jogador, em iOS, Android e web. Um Egeu gerado proceduralmente, um motor de jogo determinista e IA aplicada que narra sem nunca controlar o estado do jogo.",
        tags: ["iOS", "Android", "Web", "Motor de Jogo", "IA Aplicada"],
        badge: "EM DESENVOLVIMENTO"
      },
      viewProject: "Ver Projeto",
      comingSoon: "Mais projetos em breve"
    },
    philosophy: {
      title: "Nossa Filosofia",
      principles: {
        noHype: {
          title: "Sem Soluções Baseadas em Hype",
          description: "Escolhas tecnológicas baseadas em mérito, não em tendências."
        },
        tool: {
          title: "Tecnologia como Ferramenta",
          description: "Sistemas servem aos objetivos do negócio, não o contrário."
        },
        architecture: {
          title: "Arquitetura Primeiro",
          description: "Decisões fundamentadas em análise de risco e valor de longo prazo."
        },
        production: {
          title: "Construído para Produção",
          description: "Sistemas projetados para operar de forma confiável no mundo real."
        }
      }
    },
    contact: {
      title: "Contato",
      subtitle: "Vamos discutir como podemos ajudar com seu próximo projeto.",
      name: "Nome",
      email: "E-mail",
      message: "Mensagem",
      send: "Enviar Mensagem",
      sending: "Enviando...",
      success: "Mensagem enviada com sucesso.",
      error: "Falha ao enviar mensagem. Por favor tente novamente."
    },
    footer: {
      rights: "Todos os direitos reservados.",
      domain: "interware.dev"
    },
    portfolioIntro: {
      hero: {
        title: "Apresentação por Gonzalo Durañona",
        subtitle: "Arquitetura técnica, liderança em engenharia mobile e entrega de produtos escaláveis.",
        ctaPrimary: "Contatar Gonzalo",
        ctaSecondary: "Visitar site",
        privateNote: "Link privado."
      },
      whatWeDo: {
        title: "Liderança de engenharia e entrega prática",
        description: "Interware ajuda equipes a projetar e entregar sistemas mobile e cloud confiáveis. Combinamos arquitetura pragmática com execução sólida: segurança, desempenho, processos de entrega limpos e engenharia pronta para produção."
      },
      focusAreas: {
        title: "Áreas de foco",
        items: [
          "Arquitetura mobile (iOS/Android nativo, Swift/SwiftUI, Kotlin, React Native)",
          "Backend & cloud (APIs, microserviços, AWS/GCP/Azure)",
          "Segurança & conformidade (hardening, attestation, estratégias de certificate pinning)",
          "Aceleração de entregas (CI/CD, automação de releases, observabilidade)",
          "Engenharia de produto (do protótipo à produção)"
        ]
      },
      projects: {
        title: "Projetos recentes",
        items: [
          { title: "Entrega de Mobile Banking", description: "Resiliência offline, estratégia de tratamento de erros, hardening de segurança, fluxos de release escaláveis." },
          { title: "Plataformas Mobile Empresariais", description: "Padrões de arquitetura, modularização, otimização de desempenho e manutenibilidade a longo prazo." },
          { title: "Serviços Cloud & APIs", description: "APIs seguras, automação de infraestrutura e visibilidade operacional." },
          { title: "Funcionalidades com IA", description: "IA aplicada para geração de conteúdo e fluxos de produto, com controles responsáveis." }
        ]
      },
      workingStyle: {
        title: "Direto, claro e orientado à execução",
        description: "Trabalho de perto com stakeholders e equipes de engenharia, traduzindo objetivos de negócio em planos concretos de arquitetura e entrega. Sinto-me confortável liderando equipes, desbloqueando a execução e sendo responsável pelos resultados de ponta a ponta."
      },
      contact: {
        title: "Vamos conectar",
        description: "Se você gostaria de discutir um projeto, um papel de liderança ou uma colaboração, entre em contato.",
        email: "gonzalo@interware.dev",
        linkedin: "www.linkedin.com/in/gonzalodd",
        calendar: "calendly.com/duranona-gonzalo"
      },
      footer: "© Interware — Link privado de portfolio. Não para distribuição pública."
    }
  },
  fr: {
    nav: {
      whatWeDo: "Nos Services",
      experience: "Expérience",
      portfolio: "Portfolio",
      philosophy: "Philosophie",
      contact: "Contact"
    },
    hero: {
      tagline: "Au-delà de l'IA",
      subheading: "Intelligence appliquée, architecture et exécution pour des systèmes réels.",
      portfolioSubheading: "Catalogue de nos produits.",
      viewExperience: "Voir l'Expérience",
      contact: "Contact"
    },
    whatWeDo: {
      title: "Nos Services",
      subtitle: "Conseil technique senior fournissant des solutions orientées architecture pour des systèmes complexes.",
      services: {
        mobile: {
          title: "Architecture Mobile",
          description: "Développement natif iOS, Android et multiplateforme avec architecture de niveau production."
        },
        cloud: {
          title: "Architecture Cloud",
          description: "Solutions AWS, Google Cloud et Azure conçues pour l'échelle et la fiabilité."
        },
        ai: {
          title: "IA et Intelligence Appliquée",
          description: "Systèmes d'IA pratiques et de niveau production. Pas de battage médiatique—des solutions réelles."
        },
        backend: {
          title: "Backend et Systèmes Distribués",
          description: "Architectures haute disponibilité construites pour la performance et la maintenabilité."
        },
        security: {
          title: "Sécurité et Conformité",
          description: "Sécurité de niveau entreprise pour environnements réglementés et données sensibles."
        },
        financial: {
          title: "Systèmes Financiers",
          description: "Banque, paiements et infrastructure financière avec conformité au cœur."
        },
        strategy: {
          title: "Stratégie Technique",
          description: "Conseil en architecture et orientation stratégique pour les décisions technologiques."
        }
      }
    },
    experience: {
      title: "Parcours",
      subtitle: "Deux décennies de construction de systèmes qui fonctionnent à l'échelle.",
      areas: {
        mobile: "Plateformes Mobiles",
        mobileDesc: "Développement natif iOS et Android",
        cloud: "Systèmes Cloud-Native",
        cloudDesc: "Conception d'infrastructure évolutive",
        ai: "IA Appliquée",
        aiDesc: "Intelligence de niveau production",
        financial: "Systèmes Financiers",
        financialDesc: "Expertise banque et paiements",
        security: "Architecture de Sécurité",
        securityDesc: "Authentification et conformité",
        highAvail: "Haute Disponibilité",
        highAvailDesc: "Conception pour environnements réglementés"
      }
    },
    portfolio: {
      title: "Portfolio",
      subtitle: "Travaux sélectionnés de notre pratique.",
      wishio: {
        title: "Wishio",
        description: "Application mobile multiplateforme axée sur les rappels, les moments personnels et les interactions significatives.",
        tags: ["iOS", "Android", "Architecture Mobile"]
      },
      expeditio: {
        title: "Expeditio",
        description: "Application de planification de voyage avec IA, cartes hors ligne, stockage de documents, organisation de photos, suggestions d'hôtels intelligentes et une communauté pour conseils et avis.",
        tags: ["iOS", "Android", "IA", "Voyage"]
      },
      jason: {
        title: "Jason et les Argonautes",
        description: "RPG de stratégie et d'expédition mythologique en solo, sur iOS, Android et web. Une mer Égée générée procéduralement, un moteur de jeu déterministe et une IA appliquée qui narre sans jamais gouverner l'état du jeu.",
        tags: ["iOS", "Android", "Web", "Moteur de Jeu", "IA Appliquée"],
        badge: "EN DÉVELOPPEMENT"
      },
      viewProject: "Voir le Projet",
      comingSoon: "Plus de projets à venir"
    },
    philosophy: {
      title: "Notre Philosophie",
      principles: {
        noHype: {
          title: "Pas de Solutions Basées sur le Battage",
          description: "Choix technologiques basés sur le mérite, pas sur les tendances."
        },
        tool: {
          title: "La Technologie comme Outil",
          description: "Les systèmes servent les objectifs commerciaux, pas l'inverse."
        },
        architecture: {
          title: "Architecture d'Abord",
          description: "Décisions fondées sur l'analyse des risques et la valeur à long terme."
        },
        production: {
          title: "Conçu pour la Production",
          description: "Systèmes conçus pour fonctionner de manière fiable dans le monde réel."
        }
      }
    },
    contact: {
      title: "Contact",
      subtitle: "Discutons de la façon dont nous pouvons vous aider avec votre prochain projet.",
      name: "Nom",
      email: "E-mail",
      message: "Message",
      send: "Envoyer le Message",
      sending: "Envoi en cours...",
      success: "Message envoyé avec succès.",
      error: "Échec de l'envoi du message. Veuillez réessayer."
    },
    footer: {
      rights: "Tous droits réservés.",
      domain: "interware.dev"
    },
    portfolioIntro: {
      hero: {
        title: "Présentation par Gonzalo Durañona",
        subtitle: "Architecture technique, leadership en ingénierie mobile et livraison de produits évolutifs.",
        ctaPrimary: "Contacter Gonzalo",
        ctaSecondary: "Visiter le site",
        privateNote: "Lien privé."
      },
      whatWeDo: {
        title: "Leadership en ingénierie et livraison pratique",
        description: "Interware aide les équipes à concevoir et livrer des systèmes mobiles et cloud fiables. Nous combinons une architecture pragmatique avec une exécution solide : sécurité, performance, processus de livraison propres et ingénierie prête pour la production."
      },
      focusAreas: {
        title: "Domaines d'expertise",
        items: [
          "Architecture mobile (iOS/Android natif, Swift/SwiftUI, Kotlin, React Native)",
          "Backend & cloud (APIs, microservices, AWS/GCP/Azure)",
          "Sécurité & conformité (durcissement, attestation, stratégies de certificate pinning)",
          "Accélération des livraisons (CI/CD, automatisation des releases, observabilité)",
          "Ingénierie produit (du prototype à la production)"
        ]
      },
      projects: {
        title: "Projets récents",
        items: [
          { title: "Livraison Banque Mobile", description: "Résilience hors ligne, stratégie de gestion des erreurs, durcissement de sécurité, workflows de release évolutifs." },
          { title: "Plateformes Mobiles Entreprise", description: "Patterns d'architecture, modularisation, optimisation des performances et maintenabilité à long terme." },
          { title: "Services Cloud & APIs", description: "APIs sécurisées, automatisation d'infrastructure et visibilité opérationnelle." },
          { title: "Fonctionnalités IA", description: "IA appliquée pour la génération de contenu et les workflows produit, avec des contrôles responsables." }
        ]
      },
      workingStyle: {
        title: "Direct, clair et orienté exécution",
        description: "Je travaille en étroite collaboration avec les parties prenantes et les équipes d'ingénierie, traduisant les objectifs métier en plans concrets d'architecture et de livraison. Je suis à l'aise pour diriger des équipes, débloquer l'exécution et assumer la responsabilité des résultats de bout en bout."
      },
      contact: {
        title: "Connectons-nous",
        description: "Si vous souhaitez discuter d'un projet, d'un rôle de leadership ou d'une collaboration, contactez-moi.",
        email: "gonzalo@interware.dev",
        linkedin: "www.linkedin.com/in/gonzalodd",
        calendar: "calendly.com/duranona-gonzalo"
      },
      footer: "© Interware — Lien privé de portfolio. Pas pour distribution publique."
    }
  },
  de: {
    nav: {
      whatWeDo: "Leistungen",
      experience: "Erfahrung",
      portfolio: "Portfolio",
      philosophy: "Philosophie",
      contact: "Kontakt"
    },
    hero: {
      tagline: "Jenseits von KI",
      subheading: "Angewandte Intelligenz, Architektur und Ausführung für reale Systeme.",
      portfolioSubheading: "Katalog unserer Produkte.",
      viewExperience: "Erfahrung Ansehen",
      contact: "Kontakt"
    },
    whatWeDo: {
      title: "Unsere Leistungen",
      subtitle: "Senior-Technologieberatung mit architekturgetriebenen Lösungen für komplexe Systeme.",
      services: {
        mobile: {
          title: "Mobile Architektur",
          description: "Native iOS-, Android- und plattformübergreifende Entwicklung mit produktionsreifer Architektur."
        },
        cloud: {
          title: "Cloud-Architektur",
          description: "AWS-, Google Cloud- und Azure-Lösungen für Skalierbarkeit und Zuverlässigkeit."
        },
        ai: {
          title: "KI & Angewandte Intelligenz",
          description: "Praktische, produktionsreife KI-Systeme. Kein experimenteller Hype—echte Lösungen."
        },
        backend: {
          title: "Backend & Verteilte Systeme",
          description: "Hochverfügbare Architekturen für Leistung und Wartbarkeit."
        },
        security: {
          title: "Sicherheit & Compliance",
          description: "Enterprise-Sicherheit für regulierte Umgebungen und sensible Daten."
        },
        financial: {
          title: "Finanzsysteme",
          description: "Banking, Zahlungen und Finanzinfrastruktur mit Compliance im Kern."
        },
        strategy: {
          title: "Technische Strategie",
          description: "Architekturberatung und strategische Führung für Technologieentscheidungen."
        }
      }
    },
    experience: {
      title: "Werdegang",
      subtitle: "Zwei Jahrzehnte Erfahrung im Aufbau skalierbarer Systeme.",
      areas: {
        mobile: "Mobile Plattformen",
        mobileDesc: "Native iOS- & Android-Entwicklung",
        cloud: "Cloud-Native Systeme",
        cloudDesc: "Skalierbare Infrastrukturgestaltung",
        ai: "Angewandte KI",
        aiDesc: "Produktionsreife Intelligenz",
        financial: "Finanzsysteme",
        financialDesc: "Banking- & Zahlungsexpertise",
        security: "Sicherheitsarchitektur",
        securityDesc: "Authentifizierung & Compliance",
        highAvail: "Hochverfügbarkeit",
        highAvailDesc: "Design für regulierte Umgebungen"
      }
    },
    portfolio: {
      title: "Portfolio",
      subtitle: "Ausgewählte Arbeiten aus unserer Praxis.",
      wishio: {
        title: "Wishio",
        description: "Multiplattform-Mobile-App für Erinnerungen, persönliche Momente und bedeutungsvolle Interaktionen.",
        tags: ["iOS", "Android", "Mobile Architektur"]
      },
      expeditio: {
        title: "Expeditio",
        description: "KI-gestützte Reiseplanungs-App mit Offline-Karten, Dokumentenspeicher, Fotoorganisation, intelligenten Hotelvorschlägen und einer Community für Tipps und Bewertungen.",
        tags: ["iOS", "Android", "KI", "Reisen"]
      },
      jason: {
        title: "Jason und die Argonauten",
        description: "Mythologisches Expeditions-Strategie-RPG für Einzelspieler auf iOS, Android und Web. Eine prozedural generierte Ägäis, eine deterministische Spiel-Engine und angewandte KI, die erzählt, ohne je den Spielzustand zu besitzen.",
        tags: ["iOS", "Android", "Web", "Spiel-Engine", "Angewandte KI"],
        badge: "IN ENTWICKLUNG"
      },
      viewProject: "Projekt Ansehen",
      comingSoon: "Weitere Projekte folgen"
    },
    philosophy: {
      title: "Unsere Philosophie",
      principles: {
        noHype: {
          title: "Keine Hype-getriebenen Lösungen",
          description: "Technologieentscheidungen basierend auf Verdienst, nicht auf Trends."
        },
        tool: {
          title: "Technologie als Werkzeug",
          description: "Systeme dienen Geschäftszielen, nicht umgekehrt."
        },
        architecture: {
          title: "Architektur zuerst",
          description: "Entscheidungen basierend auf Risikoanalyse und langfristigem Wert."
        },
        production: {
          title: "Für Produktion gebaut",
          description: "Systeme für zuverlässigen Betrieb in der realen Welt."
        }
      }
    },
    contact: {
      title: "Kontakt",
      subtitle: "Lassen Sie uns besprechen, wie wir bei Ihrem nächsten Projekt helfen können.",
      name: "Name",
      email: "E-Mail",
      message: "Nachricht",
      send: "Nachricht Senden",
      sending: "Wird gesendet...",
      success: "Nachricht erfolgreich gesendet.",
      error: "Nachricht konnte nicht gesendet werden. Bitte versuchen Sie es erneut."
    },
    footer: {
      rights: "Alle Rechte vorbehalten.",
      domain: "interware.dev"
    },
    portfolioIntro: {
      hero: {
        title: "Vorstellung von Gonzalo Durañona",
        subtitle: "Technische Architektur, Mobile-Engineering-Führung und Lieferung skalierbarer Produkte.",
        ctaPrimary: "Gonzalo kontaktieren",
        ctaSecondary: "Website besuchen",
        privateNote: "Privater Link."
      },
      whatWeDo: {
        title: "Engineering-Führung und praktische Lieferung",
        description: "Interware hilft Teams, zuverlässige Mobile- und Cloud-Systeme zu entwerfen und zu liefern. Wir kombinieren pragmatische Architektur mit solider Ausführung: Sicherheit, Leistung, saubere Lieferprozesse und produktionsreife Entwicklung."
      },
      focusAreas: {
        title: "Schwerpunkte",
        items: [
          "Mobile Architektur (iOS/Android nativ, Swift/SwiftUI, Kotlin, React Native)",
          "Backend & Cloud (APIs, Microservices, AWS/GCP/Azure)",
          "Sicherheit & Compliance (Härtung, Attestierung, Certificate-Pinning-Strategien)",
          "Lieferbeschleunigung (CI/CD, Release-Automatisierung, Observability)",
          "Produktentwicklung (vom Prototyp zur Produktion)"
        ]
      },
      projects: {
        title: "Aktuelle Projekte",
        items: [
          { title: "Mobile Banking Lieferung", description: "Offline-Resilienz, Fehlerbehandlungsstrategie, Sicherheitshärtung, skalierbare Release-Workflows." },
          { title: "Enterprise Mobile Plattformen", description: "Architekturmuster, Modularisierung, Leistungsoptimierung und langfristige Wartbarkeit." },
          { title: "Cloud Services & APIs", description: "Sichere APIs, Infrastruktur-Automatisierung und betriebliche Sichtbarkeit." },
          { title: "KI-gestützte Funktionen", description: "Angewandte KI für Content-Generierung und Produkt-Workflows mit verantwortungsvollen Kontrollen." }
        ]
      },
      workingStyle: {
        title: "Direkt, klar und ausführungsorientiert",
        description: "Ich arbeite eng mit Stakeholdern und Engineering-Teams zusammen und übersetze Geschäftsziele in konkrete Architektur- und Lieferpläne. Ich fühle mich wohl dabei, Teams zu führen, die Ausführung zu entsperren und Ergebnisse von Anfang bis Ende zu verantworten."
      },
      contact: {
        title: "Lassen Sie uns verbinden",
        description: "Wenn Sie ein Projekt, eine Führungsrolle oder eine Zusammenarbeit besprechen möchten, kontaktieren Sie mich.",
        email: "gonzalo@interware.dev",
        linkedin: "www.linkedin.com/in/gonzalodd",
        calendar: "calendly.com/duranona-gonzalo"
      },
      footer: "© Interware — Privater Portfolio-Link. Nicht zur öffentlichen Verteilung."
    }
  },
  it: {
    nav: {
      whatWeDo: "Servizi",
      experience: "Esperienza",
      portfolio: "Portfolio",
      philosophy: "Filosofia",
      contact: "Contatto"
    },
    hero: {
      tagline: "Oltre l'IA",
      subheading: "Intelligenza applicata, architettura ed esecuzione per sistemi del mondo reale.",
      portfolioSubheading: "Catalogo dei nostri prodotti.",
      viewExperience: "Vedi Esperienza",
      contact: "Contatto"
    },
    whatWeDo: {
      title: "I Nostri Servizi",
      subtitle: "Consulenza tecnica senior che fornisce soluzioni orientate all'architettura per sistemi complessi.",
      services: {
        mobile: {
          title: "Architettura Mobile",
          description: "Sviluppo nativo iOS, Android e multipiattaforma con architettura di livello produzione."
        },
        cloud: {
          title: "Architettura Cloud",
          description: "Soluzioni AWS, Google Cloud e Azure progettate per scalabilità e affidabilità."
        },
        ai: {
          title: "IA e Intelligenza Applicata",
          description: "Sistemi IA pratici e di livello produzione. Niente hype sperimentale—soluzioni reali."
        },
        backend: {
          title: "Backend e Sistemi Distribuiti",
          description: "Architetture ad alta disponibilità costruite per prestazioni e manutenibilità."
        },
        security: {
          title: "Sicurezza e Conformità",
          description: "Sicurezza di livello enterprise per ambienti regolamentati e dati sensibili."
        },
        financial: {
          title: "Sistemi Finanziari",
          description: "Banking, pagamenti e infrastruttura finanziaria con conformità al centro."
        },
        strategy: {
          title: "Strategia Tecnica",
          description: "Consulenza architettonica e guida strategica per decisioni tecnologiche."
        }
      }
    },
    experience: {
      title: "Percorso",
      subtitle: "Due decenni di costruzione di sistemi che operano su scala.",
      areas: {
        mobile: "Piattaforme Mobile",
        mobileDesc: "Sviluppo nativo iOS e Android",
        cloud: "Sistemi Cloud-Native",
        cloudDesc: "Progettazione infrastruttura scalabile",
        ai: "IA Applicata",
        aiDesc: "Intelligenza di livello produzione",
        financial: "Sistemi Finanziari",
        financialDesc: "Esperienza banking e pagamenti",
        security: "Architettura di Sicurezza",
        securityDesc: "Autenticazione e conformità",
        highAvail: "Alta Disponibilità",
        highAvailDesc: "Progettazione per ambienti regolamentati"
      }
    },
    portfolio: {
      title: "Portfolio",
      subtitle: "Lavori selezionati dalla nostra pratica.",
      wishio: {
        title: "Wishio",
        description: "Applicazione mobile multipiattaforma focalizzata su promemoria, momenti personali e interazioni significative.",
        tags: ["iOS", "Android", "Architettura Mobile"]
      },
      viewProject: "Vedi Progetto",
      comingSoon: "Altri progetti in arrivo"
    },
    philosophy: {
      title: "La Nostra Filosofia",
      principles: {
        noHype: {
          title: "Niente Soluzioni Basate sull'Hype",
          description: "Scelte tecnologiche basate sul merito, non sulle tendenze."
        },
        tool: {
          title: "Tecnologia come Strumento",
          description: "I sistemi servono gli obiettivi aziendali, non il contrario."
        },
        architecture: {
          title: "Architettura Prima",
          description: "Decisioni basate su analisi del rischio e valore a lungo termine."
        },
        production: {
          title: "Costruito per la Produzione",
          description: "Sistemi progettati per operare in modo affidabile nel mondo reale."
        }
      }
    },
    contact: {
      title: "Contatto",
      subtitle: "Discutiamo di come possiamo aiutarti con il tuo prossimo progetto.",
      name: "Nome",
      email: "E-mail",
      message: "Messaggio",
      send: "Invia Messaggio",
      sending: "Invio in corso...",
      success: "Messaggio inviato con successo.",
      error: "Invio del messaggio fallito. Riprova."
    },
    footer: {
      rights: "Tutti i diritti riservati.",
      domain: "interware.dev"
    }
  },
  zh: {
    nav: {
      whatWeDo: "服务内容",
      experience: "经验",
      portfolio: "作品集",
      philosophy: "理念",
      contact: "联系我们"
    },
    hero: {
      tagline: "超越人工智能",
      subheading: "为现实世界系统提供应用智能、架构和执行。",
      portfolioSubheading: "我们的产品目录。",
      viewExperience: "查看经验",
      contact: "联系我们"
    },
    whatWeDo: {
      title: "服务内容",
      subtitle: "高级技术咨询，为复杂系统提供架构驱动的解决方案。",
      services: {
        mobile: {
          title: "移动端架构",
          description: "具有生产级架构的原生iOS、Android和跨平台开发。"
        },
        cloud: {
          title: "云架构",
          description: "为规模和可靠性设计的AWS、Google Cloud和Azure解决方案。"
        },
        ai: {
          title: "人工智能与应用智能",
          description: "实用的生产级AI系统。没有实验性炒作——真正的解决方案。"
        },
        backend: {
          title: "后端与分布式系统",
          description: "为性能和可维护性构建的高可用架构。"
        },
        security: {
          title: "安全与合规",
          description: "为受监管环境和敏感数据提供企业级安全。"
        },
        financial: {
          title: "金融系统",
          description: "以合规为核心的银行、支付和金融基础设施。"
        },
        strategy: {
          title: "技术战略",
          description: "为技术决策提供架构咨询和战略指导。"
        }
      }
    },
    experience: {
      title: "历程",
      subtitle: "二十年构建规模化运营系统的经验。",
      areas: {
        mobile: "移动平台",
        mobileDesc: "原生iOS和Android开发",
        cloud: "云原生系统",
        cloudDesc: "可扩展基础设施设计",
        ai: "应用人工智能",
        aiDesc: "生产级智能",
        financial: "金融系统",
        financialDesc: "银行与支付专业知识",
        security: "安全架构",
        securityDesc: "认证与合规",
        highAvail: "高可用性",
        highAvailDesc: "受监管环境设计"
      }
    },
    portfolio: {
      title: "作品集",
      subtitle: "我们实践中精选的作品。",
      wishio: {
        title: "Wishio",
        description: "专注于提醒、个人时刻和有意义互动的多平台移动应用。",
        tags: ["iOS", "Android", "移动架构"]
      },
      viewProject: "查看项目",
      comingSoon: "更多项目即将推出"
    },
    philosophy: {
      title: "我们的理念",
      principles: {
        noHype: {
          title: "拒绝炒作驱动的解决方案",
          description: "基于价值而非趋势的技术选择。"
        },
        tool: {
          title: "技术作为工具",
          description: "系统服务于业务目标，而非相反。"
        },
        architecture: {
          title: "架构优先",
          description: "基于风险分析和长期价值的决策。"
        },
        production: {
          title: "为生产环境而建",
          description: "设计在现实世界中可靠运行的系统。"
        }
      }
    },
    contact: {
      title: "联系我们",
      subtitle: "让我们讨论如何帮助您的下一个项目。",
      name: "姓名",
      email: "电子邮件",
      message: "留言",
      send: "发送消息",
      sending: "发送中...",
      success: "消息发送成功。",
      error: "消息发送失败。请重试。"
    },
    footer: {
      rights: "版权所有。",
      domain: "interware.dev"
    }
  },
  ja: {
    nav: {
      whatWeDo: "サービス",
      experience: "経験",
      portfolio: "ポートフォリオ",
      philosophy: "理念",
      contact: "お問い合わせ"
    },
    hero: {
      tagline: "AIを超えて",
      subheading: "現実世界のシステムに向けた応用インテリジェンス、アーキテクチャ、実行。",
      portfolioSubheading: "私たちのプロダクトカタログ。",
      viewExperience: "経験を見る",
      contact: "お問い合わせ"
    },
    whatWeDo: {
      title: "サービス内容",
      subtitle: "複雑なシステムに向けたアーキテクチャ駆動のソリューションを提供するシニア技術コンサルティング。",
      services: {
        mobile: {
          title: "モバイルアーキテクチャ",
          description: "本番グレードのアーキテクチャによるネイティブiOS、Android、クロスプラットフォーム開発。"
        },
        cloud: {
          title: "クラウドアーキテクチャ",
          description: "スケールと信頼性のために設計されたAWS、Google Cloud、Azureソリューション。"
        },
        ai: {
          title: "AI & 応用インテリジェンス",
          description: "実用的で本番グレードのAIシステム。実験的な誇大宣伝ではなく、実際のソリューション。"
        },
        backend: {
          title: "バックエンド & 分散システム",
          description: "パフォーマンスと保守性のために構築された高可用性アーキテクチャ。"
        },
        security: {
          title: "セキュリティ & コンプライアンス",
          description: "規制環境と機密データのためのエンタープライズグレードのセキュリティ。"
        },
        financial: {
          title: "金融システム",
          description: "コンプライアンスを核とした銀行、決済、金融インフラ。"
        },
        strategy: {
          title: "技術戦略",
          description: "技術決定のためのアーキテクチャコンサルティングと戦略的ガイダンス。"
        }
      }
    },
    experience: {
      title: "実績",
      subtitle: "スケールで運用するシステムを構築してきた20年の経験。",
      areas: {
        mobile: "モバイルプラットフォーム",
        mobileDesc: "ネイティブiOS & Android開発",
        cloud: "クラウドネイティブシステム",
        cloudDesc: "スケーラブルなインフラ設計",
        ai: "応用AI",
        aiDesc: "本番グレードのインテリジェンス",
        financial: "金融システム",
        financialDesc: "銀行 & 決済の専門知識",
        security: "セキュリティアーキテクチャ",
        securityDesc: "認証 & コンプライアンス",
        highAvail: "高可用性",
        highAvailDesc: "規制環境向け設計"
      }
    },
    portfolio: {
      title: "ポートフォリオ",
      subtitle: "私たちの実践から選ばれた作品。",
      wishio: {
        title: "Wishio",
        description: "リマインダー、個人的な瞬間、思いやりのあるインタラクションに焦点を当てたマルチプラットフォームモバイルアプリ。",
        tags: ["iOS", "Android", "モバイルアーキテクチャ"]
      },
      viewProject: "プロジェクトを見る",
      comingSoon: "さらなるプロジェクトを近日公開"
    },
    philosophy: {
      title: "私たちの理念",
      principles: {
        noHype: {
          title: "誇大宣伝に基づかないソリューション",
          description: "トレンドではなく、メリットに基づいた技術選択。"
        },
        tool: {
          title: "ツールとしての技術",
          description: "システムはビジネス目標に奉仕し、その逆ではない。"
        },
        architecture: {
          title: "アーキテクチャファースト",
          description: "リスク分析と長期的価値に基づいた意思決定。"
        },
        production: {
          title: "本番向けに構築",
          description: "現実世界で信頼性高く運用されるよう設計されたシステム。"
        }
      }
    },
    contact: {
      title: "お問い合わせ",
      subtitle: "次のプロジェクトでどのようにお手伝いできるか話し合いましょう。",
      name: "お名前",
      email: "メールアドレス",
      message: "メッセージ",
      send: "メッセージを送信",
      sending: "送信中...",
      success: "メッセージが正常に送信されました。",
      error: "メッセージの送信に失敗しました。もう一度お試しください。"
    },
    footer: {
      rights: "全著作権所有。",
      domain: "interware.dev"
    }
  },
  ko: {
    nav: {
      whatWeDo: "서비스",
      experience: "경험",
      portfolio: "포트폴리오",
      philosophy: "철학",
      contact: "연락처"
    },
    hero: {
      tagline: "AI를 넘어서",
      subheading: "실제 시스템을 위한 응용 인텔리전스, 아키텍처 및 실행.",
      portfolioSubheading: "우리의 제품 카탈로그.",
      viewExperience: "경험 보기",
      contact: "연락하기"
    },
    whatWeDo: {
      title: "서비스 내용",
      subtitle: "복잡한 시스템을 위한 아키텍처 기반 솔루션을 제공하는 시니어 기술 컨설팅.",
      services: {
        mobile: {
          title: "모바일 아키텍처",
          description: "프로덕션 수준 아키텍처를 갖춘 네이티브 iOS, Android 및 크로스 플랫폼 개발."
        },
        cloud: {
          title: "클라우드 아키텍처",
          description: "확장성과 안정성을 위해 설계된 AWS, Google Cloud, Azure 솔루션."
        },
        ai: {
          title: "AI & 응용 인텔리전스",
          description: "실용적이고 프로덕션 수준의 AI 시스템. 실험적 과장 없이—진짜 솔루션."
        },
        backend: {
          title: "백엔드 & 분산 시스템",
          description: "성능과 유지보수성을 위해 구축된 고가용성 아키텍처."
        },
        security: {
          title: "보안 & 컴플라이언스",
          description: "규제 환경과 민감한 데이터를 위한 엔터프라이즈급 보안."
        },
        financial: {
          title: "금융 시스템",
          description: "컴플라이언스를 핵심으로 한 뱅킹, 결제 및 금융 인프라."
        },
        strategy: {
          title: "기술 전략",
          description: "기술 결정을 위한 아키텍처 컨설팅과 전략적 가이던스."
        }
      }
    },
    experience: {
      title: "이력",
      subtitle: "규모에서 운영되는 시스템을 구축해온 20년의 경험.",
      areas: {
        mobile: "모바일 플랫폼",
        mobileDesc: "네이티브 iOS & Android 개발",
        cloud: "클라우드 네이티브 시스템",
        cloudDesc: "확장 가능한 인프라 설계",
        ai: "응용 AI",
        aiDesc: "프로덕션 수준 인텔리전스",
        financial: "금융 시스템",
        financialDesc: "뱅킹 & 결제 전문성",
        security: "보안 아키텍처",
        securityDesc: "인증 & 컴플라이언스",
        highAvail: "고가용성",
        highAvailDesc: "규제 환경 설계"
      }
    },
    portfolio: {
      title: "포트폴리오",
      subtitle: "우리 실무에서 선정된 작업물.",
      wishio: {
        title: "Wishio",
        description: "리마인더, 개인적인 순간, 의미 있는 상호작용에 초점을 맞춘 멀티플랫폼 모바일 앱.",
        tags: ["iOS", "Android", "모바일 아키텍처"]
      },
      viewProject: "프로젝트 보기",
      comingSoon: "더 많은 프로젝트가 곧 공개됩니다"
    },
    philosophy: {
      title: "우리의 철학",
      principles: {
        noHype: {
          title: "과장 기반 솔루션 없음",
          description: "트렌드가 아닌 가치에 기반한 기술 선택."
        },
        tool: {
          title: "도구로서의 기술",
          description: "시스템은 비즈니스 목표를 위해 봉사하며, 그 반대가 아님."
        },
        architecture: {
          title: "아키텍처 우선",
          description: "위험 분석과 장기 가치에 기반한 결정."
        },
        production: {
          title: "프로덕션을 위해 구축",
          description: "실제 세계에서 안정적으로 운영되도록 설계된 시스템."
        }
      }
    },
    contact: {
      title: "연락처",
      subtitle: "다음 프로젝트에서 어떻게 도움을 드릴 수 있는지 논의해 봅시다.",
      name: "이름",
      email: "이메일",
      message: "메시지",
      send: "메시지 보내기",
      sending: "전송 중...",
      success: "메시지가 성공적으로 전송되었습니다.",
      error: "메시지 전송에 실패했습니다. 다시 시도해 주세요."
    },
    footer: {
      rights: "모든 권리 보유.",
      domain: "interware.dev"
    }
  },
  ar: {
    nav: {
      whatWeDo: "خدماتنا",
      experience: "الخبرة",
      portfolio: "أعمالنا",
      philosophy: "فلسفتنا",
      contact: "اتصل بنا"
    },
    hero: {
      tagline: "ما وراء الذكاء الاصطناعي",
      subheading: "ذكاء تطبيقي وهندسة معمارية وتنفيذ لأنظمة العالم الحقيقي.",
      portfolioSubheading: "كتالوج منتجاتنا.",
      viewExperience: "عرض الخبرة",
      contact: "اتصل بنا"
    },
    whatWeDo: {
      title: "خدماتنا",
      subtitle: "استشارات تقنية متقدمة تقدم حلولاً قائمة على الهندسة المعمارية للأنظمة المعقدة.",
      services: {
        mobile: {
          title: "هندسة الموبايل",
          description: "تطوير iOS وAndroid الأصلي ومتعدد المنصات بهندسة معمارية جاهزة للإنتاج."
        },
        cloud: {
          title: "هندسة السحابة",
          description: "حلول AWS وGoogle Cloud وAzure مصممة للتوسع والموثوقية."
        },
        ai: {
          title: "الذكاء الاصطناعي والذكاء التطبيقي",
          description: "أنظمة ذكاء اصطناعي عملية وجاهزة للإنتاج. بدون ضجة تجريبية—حلول حقيقية."
        },
        backend: {
          title: "الخلفية والأنظمة الموزعة",
          description: "هندسة معمارية عالية التوفر مبنية للأداء وقابلية الصيانة."
        },
        security: {
          title: "الأمان والامتثال",
          description: "أمان على مستوى المؤسسات للبيئات المنظمة والبيانات الحساسة."
        },
        financial: {
          title: "الأنظمة المالية",
          description: "الخدمات المصرفية والمدفوعات والبنية التحتية المالية مع الامتثال في الصميم."
        },
        strategy: {
          title: "الاستراتيجية التقنية",
          description: "استشارات معمارية وتوجيه استراتيجي للقرارات التقنية."
        }
      }
    },
    experience: {
      title: "السجل",
      subtitle: "عقدان من بناء الأنظمة التي تعمل على نطاق واسع.",
      areas: {
        mobile: "منصات الموبايل",
        mobileDesc: "تطوير iOS وAndroid الأصلي",
        cloud: "الأنظمة السحابية الأصلية",
        cloudDesc: "تصميم بنية تحتية قابلة للتوسع",
        ai: "الذكاء الاصطناعي التطبيقي",
        aiDesc: "ذكاء جاهز للإنتاج",
        financial: "الأنظمة المالية",
        financialDesc: "خبرة في المصارف والمدفوعات",
        security: "هندسة الأمان",
        securityDesc: "المصادقة والامتثال",
        highAvail: "التوفر العالي",
        highAvailDesc: "تصميم للبيئات المنظمة"
      }
    },
    portfolio: {
      title: "أعمالنا",
      subtitle: "أعمال مختارة من ممارستنا.",
      wishio: {
        title: "Wishio",
        description: "تطبيق موبايل متعدد المنصات يركز على التذكيرات واللحظات الشخصية والتفاعلات المعنوية.",
        tags: ["iOS", "Android", "هندسة الموبايل"]
      },
      viewProject: "عرض المشروع",
      comingSoon: "المزيد من المشاريع قريباً"
    },
    philosophy: {
      title: "فلسفتنا",
      principles: {
        noHype: {
          title: "بدون حلول قائمة على الضجة",
          description: "اختيارات تقنية مبنية على الجدارة، وليس الاتجاهات."
        },
        tool: {
          title: "التقنية كأداة",
          description: "الأنظمة تخدم أهداف العمل، وليس العكس."
        },
        architecture: {
          title: "الهندسة المعمارية أولاً",
          description: "قرارات مبنية على تحليل المخاطر والقيمة طويلة المدى."
        },
        production: {
          title: "مبني للإنتاج",
          description: "أنظمة مصممة للعمل بموثوقية في العالم الحقيقي."
        }
      }
    },
    contact: {
      title: "اتصل بنا",
      subtitle: "دعنا نناقش كيف يمكننا المساعدة في مشروعك القادم.",
      name: "الاسم",
      email: "البريد الإلكتروني",
      message: "الرسالة",
      send: "إرسال الرسالة",
      sending: "جاري الإرسال...",
      success: "تم إرسال الرسالة بنجاح.",
      error: "فشل إرسال الرسالة. يرجى المحاولة مرة أخرى."
    },
    footer: {
      rights: "جميع الحقوق محفوظة.",
      domain: "interware.dev"
    }
  },
  hi: {
    nav: {
      whatWeDo: "हम क्या करते हैं",
      experience: "अनुभव",
      portfolio: "पोर्टफोलियो",
      philosophy: "दर्शन",
      contact: "संपर्क करें"
    },
    hero: {
      tagline: "AI से परे",
      subheading: "वास्तविक दुनिया के सिस्टम के लिए अनुप्रयुक्त बुद्धिमत्ता, आर्किटेक्चर और निष्पादन।",
      portfolioSubheading: "हमारे उत्पादों की सूची।",
      viewExperience: "अनुभव देखें",
      contact: "संपर्क करें"
    },
    whatWeDo: {
      title: "हम क्या करते हैं",
      subtitle: "जटिल सिस्टम के लिए आर्किटेक्चर-संचालित समाधान प्रदान करने वाली वरिष्ठ तकनीकी परामर्श।",
      services: {
        mobile: {
          title: "मोबाइल आर्किटेक्चर",
          description: "उत्पादन-ग्रेड आर्किटेक्चर के साथ नेटिव iOS, Android और क्रॉस-प्लेटफॉर्म डेवलपमेंट।"
        },
        cloud: {
          title: "क्लाउड आर्किटेक्चर",
          description: "स्केल और विश्वसनीयता के लिए डिज़ाइन किए गए AWS, Google Cloud और Azure समाधान।"
        },
        ai: {
          title: "AI और अनुप्रयुक्त बुद्धिमत्ता",
          description: "व्यावहारिक, उत्पादन-ग्रेड AI सिस्टम। कोई प्रयोगात्मक हाइप नहीं—असली समाधान।"
        },
        backend: {
          title: "बैकएंड और वितरित सिस्टम",
          description: "प्रदर्शन और रखरखाव के लिए निर्मित उच्च-उपलब्धता आर्किटेक्चर।"
        },
        security: {
          title: "सुरक्षा और अनुपालन",
          description: "विनियमित वातावरण और संवेदनशील डेटा के लिए एंटरप्राइज़-ग्रेड सुरक्षा।"
        },
        financial: {
          title: "वित्तीय सिस्टम",
          description: "अनुपालन को कोर में रखते हुए बैंकिंग, भुगतान और वित्तीय बुनियादी ढांचा।"
        },
        strategy: {
          title: "तकनीकी रणनीति",
          description: "प्रौद्योगिकी निर्णयों के लिए आर्किटेक्चर परामर्श और रणनीतिक मार्गदर्शन।"
        }
      }
    },
    experience: {
      title: "अनुभव",
      subtitle: "स्केल पर संचालित होने वाले सिस्टम बनाने के दो दशक।",
      areas: {
        mobile: "मोबाइल प्लेटफॉर्म",
        mobileDesc: "नेटिव iOS और Android डेवलपमेंट",
        cloud: "क्लाउड-नेटिव सिस्टम",
        cloudDesc: "स्केलेबल इंफ्रास्ट्रक्चर डिज़ाइन",
        ai: "अनुप्रयुक्त AI",
        aiDesc: "उत्पादन-ग्रेड बुद्धिमत्ता",
        financial: "वित्तीय सिस्टम",
        financialDesc: "बैंकिंग और भुगतान विशेषज्ञता",
        security: "सुरक्षा आर्किटेक्चर",
        securityDesc: "प्रमाणीकरण और अनुपालन",
        highAvail: "उच्च उपलब्धता",
        highAvailDesc: "विनियमित वातावरण डिज़ाइन"
      }
    },
    portfolio: {
      title: "पोर्टफोलियो",
      subtitle: "हमारी प्रैक्टिस से चुनिंदा काम।",
      wishio: {
        title: "Wishio",
        description: "रिमाइंडर, व्यक्तिगत क्षणों और सार्थक बातचीत पर केंद्रित मल्टी-प्लेटफॉर्म मोबाइल एप्लिकेशन।",
        tags: ["iOS", "Android", "मोबाइल आर्किटेक्चर"]
      },
      viewProject: "प्रोजेक्ट देखें",
      comingSoon: "और प्रोजेक्ट जल्द आ रहे हैं"
    },
    philosophy: {
      title: "हमारा दर्शन",
      principles: {
        noHype: {
          title: "हाइप-संचालित समाधान नहीं",
          description: "ट्रेंड्स नहीं, योग्यता पर आधारित प्रौद्योगिकी विकल्प।"
        },
        tool: {
          title: "एक उपकरण के रूप में प्रौद्योगिकी",
          description: "सिस्टम व्यावसायिक उद्देश्यों की सेवा करते हैं, इसके विपरीत नहीं।"
        },
        architecture: {
          title: "आर्किटेक्चर पहले",
          description: "जोखिम विश्लेषण और दीर्घकालिक मूल्य पर आधारित निर्णय।"
        },
        production: {
          title: "उत्पादन के लिए निर्मित",
          description: "वास्तविक दुनिया में विश्वसनीय रूप से संचालित होने के लिए डिज़ाइन किए गए सिस्टम।"
        }
      }
    },
    contact: {
      title: "संपर्क करें",
      subtitle: "आइए चर्चा करें कि हम आपके अगले प्रोजेक्ट में कैसे मदद कर सकते हैं।",
      name: "नाम",
      email: "ईमेल",
      message: "संदेश",
      send: "संदेश भेजें",
      sending: "भेज रहे हैं...",
      success: "संदेश सफलतापूर्वक भेजा गया।",
      error: "संदेश भेजने में विफल। कृपया पुनः प्रयास करें।"
    },
    footer: {
      rights: "सर्वाधिकार सुरक्षित।",
      domain: "interware.dev"
    }
  },
  ru: {
    nav: {
      whatWeDo: "Услуги",
      experience: "Опыт",
      portfolio: "Портфолио",
      philosophy: "Философия",
      contact: "Контакты"
    },
    hero: {
      tagline: "За пределами ИИ",
      subheading: "Прикладной интеллект, архитектура и реализация для систем реального мира.",
      portfolioSubheading: "Каталог наших продуктов.",
      viewExperience: "Посмотреть опыт",
      contact: "Связаться"
    },
    whatWeDo: {
      title: "Наши услуги",
      subtitle: "Старший технический консалтинг, предоставляющий архитектурно-ориентированные решения для сложных систем.",
      services: {
        mobile: {
          title: "Мобильная архитектура",
          description: "Нативная разработка iOS, Android и кроссплатформенная с архитектурой производственного уровня."
        },
        cloud: {
          title: "Облачная архитектура",
          description: "Решения AWS, Google Cloud и Azure, разработанные для масштабируемости и надёжности."
        },
        ai: {
          title: "ИИ и прикладной интеллект",
          description: "Практичные системы ИИ производственного уровня. Никакой экспериментальной шумихи—реальные решения."
        },
        backend: {
          title: "Бэкенд и распределённые системы",
          description: "Высокодоступные архитектуры, созданные для производительности и поддерживаемости."
        },
        security: {
          title: "Безопасность и соответствие",
          description: "Безопасность корпоративного уровня для регулируемых сред и конфиденциальных данных."
        },
        financial: {
          title: "Финансовые системы",
          description: "Банкинг, платежи и финансовая инфраструктура с соответствием требованиям в основе."
        },
        strategy: {
          title: "Техническая стратегия",
          description: "Архитектурный консалтинг и стратегическое руководство для технологических решений."
        }
      }
    },
    experience: {
      title: "Опыт",
      subtitle: "Два десятилетия создания систем, работающих в масштабе.",
      areas: {
        mobile: "Мобильные платформы",
        mobileDesc: "Нативная разработка iOS и Android",
        cloud: "Облачно-нативные системы",
        cloudDesc: "Проектирование масштабируемой инфраструктуры",
        ai: "Прикладной ИИ",
        aiDesc: "Интеллект производственного уровня",
        financial: "Финансовые системы",
        financialDesc: "Экспертиза в банкинге и платежах",
        security: "Архитектура безопасности",
        securityDesc: "Аутентификация и соответствие",
        highAvail: "Высокая доступность",
        highAvailDesc: "Проектирование для регулируемых сред"
      }
    },
    portfolio: {
      title: "Портфолио",
      subtitle: "Избранные работы из нашей практики.",
      wishio: {
        title: "Wishio",
        description: "Мультиплатформенное мобильное приложение, ориентированное на напоминания, личные моменты и значимые взаимодействия.",
        tags: ["iOS", "Android", "Мобильная архитектура"]
      },
      viewProject: "Посмотреть проект",
      comingSoon: "Больше проектов скоро"
    },
    philosophy: {
      title: "Наша философия",
      principles: {
        noHype: {
          title: "Никаких решений на основе шумихи",
          description: "Технологический выбор на основе достоинств, а не трендов."
        },
        tool: {
          title: "Технология как инструмент",
          description: "Системы служат бизнес-целям, а не наоборот."
        },
        architecture: {
          title: "Сначала архитектура",
          description: "Решения, основанные на анализе рисков и долгосрочной ценности."
        },
        production: {
          title: "Создано для продакшена",
          description: "Системы, спроектированные для надёжной работы в реальном мире."
        }
      }
    },
    contact: {
      title: "Контакты",
      subtitle: "Давайте обсудим, как мы можем помочь с вашим следующим проектом.",
      name: "Имя",
      email: "Эл. почта",
      message: "Сообщение",
      send: "Отправить сообщение",
      sending: "Отправка...",
      success: "Сообщение успешно отправлено.",
      error: "Не удалось отправить сообщение. Пожалуйста, попробуйте снова."
    },
    footer: {
      rights: "Все права защищены.",
      domain: "interware.dev"
    }
  },
  tr: {
    nav: {
      whatWeDo: "Hizmetler",
      experience: "Deneyim",
      portfolio: "Portföy",
      philosophy: "Felsefe",
      contact: "İletişim"
    },
    hero: {
      tagline: "Yapay Zekanın Ötesinde",
      subheading: "Gerçek dünya sistemleri için uygulamalı zeka, mimari ve uygulama.",
      portfolioSubheading: "Ürünlerimizin kataloğu.",
      viewExperience: "Deneyimi Gör",
      contact: "İletişim"
    },
    whatWeDo: {
      title: "Hizmetlerimiz",
      subtitle: "Karmaşık sistemler için mimari odaklı çözümler sunan kıdemli teknik danışmanlık.",
      services: {
        mobile: {
          title: "Mobil Mimari",
          description: "Üretim düzeyinde mimari ile yerli iOS, Android ve çapraz platform geliştirme."
        },
        cloud: {
          title: "Bulut Mimarisi",
          description: "Ölçek ve güvenilirlik için tasarlanmış AWS, Google Cloud ve Azure çözümleri."
        },
        ai: {
          title: "AI ve Uygulamalı Zeka",
          description: "Pratik, üretim düzeyinde AI sistemleri. Deneysel abartı yok—gerçek çözümler."
        },
        backend: {
          title: "Backend ve Dağıtık Sistemler",
          description: "Performans ve sürdürülebilirlik için inşa edilmiş yüksek erişilebilirlik mimarileri."
        },
        security: {
          title: "Güvenlik ve Uyumluluk",
          description: "Düzenlenmiş ortamlar ve hassas veriler için kurumsal düzeyde güvenlik."
        },
        financial: {
          title: "Finansal Sistemler",
          description: "Uyumluluk çekirdeğinde olan bankacılık, ödemeler ve finans altyapısı."
        },
        strategy: {
          title: "Teknik Strateji",
          description: "Teknoloji kararları için mimari danışmanlık ve stratejik rehberlik."
        }
      }
    },
    experience: {
      title: "Deneyim",
      subtitle: "Ölçekte çalışan sistemler inşa etmenin yirmi yılı.",
      areas: {
        mobile: "Mobil Platformlar",
        mobileDesc: "Yerli iOS ve Android geliştirme",
        cloud: "Bulut-Yerli Sistemler",
        cloudDesc: "Ölçeklenebilir altyapı tasarımı",
        ai: "Uygulamalı AI",
        aiDesc: "Üretim düzeyinde zeka",
        financial: "Finansal Sistemler",
        financialDesc: "Bankacılık ve ödeme uzmanlığı",
        security: "Güvenlik Mimarisi",
        securityDesc: "Kimlik doğrulama ve uyumluluk",
        highAvail: "Yüksek Erişilebilirlik",
        highAvailDesc: "Düzenlenmiş ortamlar için tasarım"
      }
    },
    portfolio: {
      title: "Portföy",
      subtitle: "Uygulamamızdan seçilmiş çalışmalar.",
      wishio: {
        title: "Wishio",
        description: "Hatırlatıcılar, kişisel anlar ve anlamlı etkileşimlere odaklanan çok platformlu mobil uygulama.",
        tags: ["iOS", "Android", "Mobil Mimari"]
      },
      viewProject: "Projeyi Gör",
      comingSoon: "Daha fazla proje yakında"
    },
    philosophy: {
      title: "Felsefemiz",
      principles: {
        noHype: {
          title: "Abartıya Dayalı Çözümler Yok",
          description: "Trendlere değil, liyakate dayalı teknoloji seçimleri."
        },
        tool: {
          title: "Araç Olarak Teknoloji",
          description: "Sistemler iş hedeflerine hizmet eder, tam tersi değil."
        },
        architecture: {
          title: "Önce Mimari",
          description: "Risk analizi ve uzun vadeli değere dayalı kararlar."
        },
        production: {
          title: "Üretim için İnşa Edilmiş",
          description: "Gerçek dünyada güvenilir şekilde çalışmak üzere tasarlanmış sistemler."
        }
      }
    },
    contact: {
      title: "İletişim",
      subtitle: "Bir sonraki projenizde nasıl yardımcı olabileceğimizi tartışalım.",
      name: "İsim",
      email: "E-posta",
      message: "Mesaj",
      send: "Mesaj Gönder",
      sending: "Gönderiliyor...",
      success: "Mesaj başarıyla gönderildi.",
      error: "Mesaj gönderilemedi. Lütfen tekrar deneyin."
    },
    footer: {
      rights: "Tüm hakları saklıdır.",
      domain: "interware.dev"
    }
  },
  nl: {
    nav: {
      whatWeDo: "Diensten",
      experience: "Ervaring",
      portfolio: "Portfolio",
      philosophy: "Filosofie",
      contact: "Contact"
    },
    hero: {
      tagline: "Voorbij AI",
      subheading: "Toegepaste intelligentie, architectuur en uitvoering voor real-world systemen.",
      portfolioSubheading: "Catalogus van onze producten.",
      viewExperience: "Bekijk Ervaring",
      contact: "Contact"
    },
    whatWeDo: {
      title: "Onze Diensten",
      subtitle: "Senior technisch advies dat architectuurgedreven oplossingen levert voor complexe systemen.",
      services: {
        mobile: {
          title: "Mobiele Architectuur",
          description: "Native iOS-, Android- en cross-platform ontwikkeling met productie-grade architectuur."
        },
        cloud: {
          title: "Cloud Architectuur",
          description: "AWS-, Google Cloud- en Azure-oplossingen ontworpen voor schaalbaarheid en betrouwbaarheid."
        },
        ai: {
          title: "AI & Toegepaste Intelligentie",
          description: "Praktische, productie-grade AI-systemen. Geen experimentele hype—echte oplossingen."
        },
        backend: {
          title: "Backend & Gedistribueerde Systemen",
          description: "Hoog-beschikbare architecturen gebouwd voor prestaties en onderhoudbaarheid."
        },
        security: {
          title: "Beveiliging & Compliance",
          description: "Enterprise-grade beveiliging voor gereguleerde omgevingen en gevoelige gegevens."
        },
        financial: {
          title: "Financiële Systemen",
          description: "Banking, betalingen en financiële infrastructuur met compliance in de kern."
        },
        strategy: {
          title: "Technische Strategie",
          description: "Architectuuradvies en strategische begeleiding voor technologiebeslissingen."
        }
      }
    },
    experience: {
      title: "Trackrecord",
      subtitle: "Twee decennia van het bouwen van systemen die op schaal werken.",
      areas: {
        mobile: "Mobiele Platforms",
        mobileDesc: "Native iOS & Android ontwikkeling",
        cloud: "Cloud-Native Systemen",
        cloudDesc: "Schaalbaar infrastructuurontwerp",
        ai: "Toegepaste AI",
        aiDesc: "Productie-grade intelligentie",
        financial: "Financiële Systemen",
        financialDesc: "Banking & betalingsexpertise",
        security: "Beveiligingsarchitectuur",
        securityDesc: "Authenticatie & compliance",
        highAvail: "Hoge Beschikbaarheid",
        highAvailDesc: "Ontwerp voor gereguleerde omgevingen"
      }
    },
    portfolio: {
      title: "Portfolio",
      subtitle: "Geselecteerd werk uit onze praktijk.",
      wishio: {
        title: "Wishio",
        description: "Multi-platform mobiele applicatie gericht op herinneringen, persoonlijke momenten en betekenisvolle interacties.",
        tags: ["iOS", "Android", "Mobiele Architectuur"]
      },
      viewProject: "Bekijk Project",
      comingSoon: "Meer projecten binnenkort"
    },
    philosophy: {
      title: "Onze Filosofie",
      principles: {
        noHype: {
          title: "Geen Hype-Gedreven Oplossingen",
          description: "Technologiekeuzes gebaseerd op verdienste, niet op trends."
        },
        tool: {
          title: "Technologie als Hulpmiddel",
          description: "Systemen dienen bedrijfsdoelen, niet andersom."
        },
        architecture: {
          title: "Architectuur Eerst",
          description: "Beslissingen gebaseerd op risicoanalyse en langetermijnwaarde."
        },
        production: {
          title: "Gebouwd voor Productie",
          description: "Systemen ontworpen om betrouwbaar te werken in de echte wereld."
        }
      }
    },
    contact: {
      title: "Contact",
      subtitle: "Laten we bespreken hoe we kunnen helpen met uw volgende project.",
      name: "Naam",
      email: "E-mail",
      message: "Bericht",
      send: "Bericht Verzenden",
      sending: "Verzenden...",
      success: "Bericht succesvol verzonden.",
      error: "Bericht verzenden mislukt. Probeer het opnieuw."
    },
    footer: {
      rights: "Alle rechten voorbehouden.",
      domain: "interware.dev"
    }
  },
  pl: {
    nav: {
      whatWeDo: "Usługi",
      experience: "Doświadczenie",
      portfolio: "Portfolio",
      philosophy: "Filozofia",
      contact: "Kontakt"
    },
    hero: {
      tagline: "Poza AI",
      subheading: "Stosowana inteligencja, architektura i realizacja dla systemów rzeczywistego świata.",
      portfolioSubheading: "Katalog naszych produktów.",
      viewExperience: "Zobacz doświadczenie",
      contact: "Kontakt"
    },
    whatWeDo: {
      title: "Nasze usługi",
      subtitle: "Starsze doradztwo techniczne dostarczające rozwiązania zorientowane na architekturę dla złożonych systemów.",
      services: {
        mobile: {
          title: "Architektura mobilna",
          description: "Natywny rozwój iOS, Android i wieloplatformowy z architekturą klasy produkcyjnej."
        },
        cloud: {
          title: "Architektura chmury",
          description: "Rozwiązania AWS, Google Cloud i Azure zaprojektowane pod kątem skalowalności i niezawodności."
        },
        ai: {
          title: "AI i stosowana inteligencja",
          description: "Praktyczne systemy AI klasy produkcyjnej. Żadnego eksperymentalnego szumu—prawdziwe rozwiązania."
        },
        backend: {
          title: "Backend i systemy rozproszone",
          description: "Architektury o wysokiej dostępności zbudowane pod kątem wydajności i łatwości utrzymania."
        },
        security: {
          title: "Bezpieczeństwo i zgodność",
          description: "Bezpieczeństwo klasy korporacyjnej dla środowisk regulowanych i danych wrażliwych."
        },
        financial: {
          title: "Systemy finansowe",
          description: "Bankowość, płatności i infrastruktura finansowa z zgodnością w centrum."
        },
        strategy: {
          title: "Strategia techniczna",
          description: "Doradztwo architektoniczne i strategiczne wsparcie dla decyzji technologicznych."
        }
      }
    },
    experience: {
      title: "Doświadczenie",
      subtitle: "Dwie dekady budowania systemów działających na dużą skalę.",
      areas: {
        mobile: "Platformy mobilne",
        mobileDesc: "Natywny rozwój iOS i Android",
        cloud: "Systemy chmurowe",
        cloudDesc: "Projektowanie skalowalnej infrastruktury",
        ai: "Stosowana AI",
        aiDesc: "Inteligencja klasy produkcyjnej",
        financial: "Systemy finansowe",
        financialDesc: "Ekspertyza w bankowości i płatnościach",
        security: "Architektura bezpieczeństwa",
        securityDesc: "Uwierzytelnianie i zgodność",
        highAvail: "Wysoka dostępność",
        highAvailDesc: "Projektowanie dla środowisk regulowanych"
      }
    },
    portfolio: {
      title: "Portfolio",
      subtitle: "Wybrane prace z naszej praktyki.",
      wishio: {
        title: "Wishio",
        description: "Wieloplatformowa aplikacja mobilna skupiona na przypomnieniach, osobistych chwilach i znaczących interakcjach.",
        tags: ["iOS", "Android", "Architektura mobilna"]
      },
      viewProject: "Zobacz projekt",
      comingSoon: "Więcej projektów wkrótce"
    },
    philosophy: {
      title: "Nasza filozofia",
      principles: {
        noHype: {
          title: "Bez rozwiązań napędzanych szumem",
          description: "Wybory technologiczne oparte na wartości, nie na trendach."
        },
        tool: {
          title: "Technologia jako narzędzie",
          description: "Systemy służą celom biznesowym, nie odwrotnie."
        },
        architecture: {
          title: "Najpierw architektura",
          description: "Decyzje oparte na analizie ryzyka i długoterminowej wartości."
        },
        production: {
          title: "Zbudowane dla produkcji",
          description: "Systemy zaprojektowane do niezawodnego działania w rzeczywistym świecie."
        }
      }
    },
    contact: {
      title: "Kontakt",
      subtitle: "Porozmawiajmy o tym, jak możemy pomóc z Twoim następnym projektem.",
      name: "Imię",
      email: "E-mail",
      message: "Wiadomość",
      send: "Wyślij wiadomość",
      sending: "Wysyłanie...",
      success: "Wiadomość wysłana pomyślnie.",
      error: "Nie udało się wysłać wiadomości. Spróbuj ponownie."
    },
    footer: {
      rights: "Wszelkie prawa zastrzeżone.",
      domain: "interware.dev"
    }
  },
  id: {
    nav: {
      whatWeDo: "Layanan",
      experience: "Pengalaman",
      portfolio: "Portofolio",
      philosophy: "Filosofi",
      contact: "Kontak"
    },
    hero: {
      tagline: "Melampaui AI",
      subheading: "Kecerdasan terapan, arsitektur, dan eksekusi untuk sistem dunia nyata.",
      portfolioSubheading: "Katalog produk kami.",
      viewExperience: "Lihat Pengalaman",
      contact: "Kontak"
    },
    whatWeDo: {
      title: "Layanan Kami",
      subtitle: "Konsultasi teknis senior yang memberikan solusi berbasis arsitektur untuk sistem kompleks.",
      services: {
        mobile: {
          title: "Arsitektur Mobile",
          description: "Pengembangan native iOS, Android, dan cross-platform dengan arsitektur tingkat produksi."
        },
        cloud: {
          title: "Arsitektur Cloud",
          description: "Solusi AWS, Google Cloud, dan Azure yang dirancang untuk skalabilitas dan keandalan."
        },
        ai: {
          title: "AI & Kecerdasan Terapan",
          description: "Sistem AI praktis dan tingkat produksi. Tanpa hype eksperimental—solusi nyata."
        },
        backend: {
          title: "Backend & Sistem Terdistribusi",
          description: "Arsitektur ketersediaan tinggi yang dibangun untuk kinerja dan kemudahan pemeliharaan."
        },
        security: {
          title: "Keamanan & Kepatuhan",
          description: "Keamanan tingkat enterprise untuk lingkungan teregulasi dan data sensitif."
        },
        financial: {
          title: "Sistem Keuangan",
          description: "Perbankan, pembayaran, dan infrastruktur keuangan dengan kepatuhan sebagai inti."
        },
        strategy: {
          title: "Strategi Teknis",
          description: "Konsultasi arsitektur dan panduan strategis untuk keputusan teknologi."
        }
      }
    },
    experience: {
      title: "Rekam Jejak",
      subtitle: "Dua dekade membangun sistem yang beroperasi pada skala.",
      areas: {
        mobile: "Platform Mobile",
        mobileDesc: "Pengembangan native iOS & Android",
        cloud: "Sistem Cloud-Native",
        cloudDesc: "Desain infrastruktur skalabel",
        ai: "AI Terapan",
        aiDesc: "Kecerdasan tingkat produksi",
        financial: "Sistem Keuangan",
        financialDesc: "Keahlian perbankan & pembayaran",
        security: "Arsitektur Keamanan",
        securityDesc: "Otentikasi & kepatuhan",
        highAvail: "Ketersediaan Tinggi",
        highAvailDesc: "Desain untuk lingkungan teregulasi"
      }
    },
    portfolio: {
      title: "Portofolio",
      subtitle: "Karya terpilih dari praktik kami.",
      wishio: {
        title: "Wishio",
        description: "Aplikasi mobile multi-platform yang berfokus pada pengingat, momen pribadi, dan interaksi bermakna.",
        tags: ["iOS", "Android", "Arsitektur Mobile"]
      },
      viewProject: "Lihat Proyek",
      comingSoon: "Lebih banyak proyek segera hadir"
    },
    philosophy: {
      title: "Filosofi Kami",
      principles: {
        noHype: {
          title: "Tanpa Solusi Berbasis Hype",
          description: "Pilihan teknologi berdasarkan nilai, bukan tren."
        },
        tool: {
          title: "Teknologi sebagai Alat",
          description: "Sistem melayani tujuan bisnis, bukan sebaliknya."
        },
        architecture: {
          title: "Arsitektur Dulu",
          description: "Keputusan berdasarkan analisis risiko dan nilai jangka panjang."
        },
        production: {
          title: "Dibangun untuk Produksi",
          description: "Sistem yang dirancang untuk beroperasi dengan andal di dunia nyata."
        }
      }
    },
    contact: {
      title: "Kontak",
      subtitle: "Mari diskusikan bagaimana kami dapat membantu proyek Anda selanjutnya.",
      name: "Nama",
      email: "Email",
      message: "Pesan",
      send: "Kirim Pesan",
      sending: "Mengirim...",
      success: "Pesan berhasil dikirim.",
      error: "Gagal mengirim pesan. Silakan coba lagi."
    },
    footer: {
      rights: "Hak cipta dilindungi.",
      domain: "interware.dev"
    }
  }
};

export const languageNames = {
  en: "English",
  es: "Español",
  pt: "Português",
  fr: "Français",
  de: "Deutsch",
  it: "Italiano",
  zh: "中文",
  ja: "日本語",
  ko: "한국어",
  ar: "العربية",
  hi: "हिन्दी",
  ru: "Русский",
  tr: "Türkçe",
  nl: "Nederlands",
  pl: "Polski",
  id: "Bahasa Indonesia"
};

export const getDetectedLanguage = () => {
  const browserLang = navigator.language?.split('-')[0] || 'en';
  return translations[browserLang] ? browserLang : 'en';
};
